import { atom, atomFamily } from "recoil";

import { DEFAULT_LIVE_MAP_RIDE_FILTER } from "../../constants";
import {
  LiveMapDisplayModeEnum,
  LiveMapMobileSurfaceEnum,
  LiveMapRideFilterEnum,
  RideDelayStatusEnum,
} from "../../enums";
import {
  emptyLiveMapGridToolbarStats,
  ILiveMapGridToolbarStats,
  RydeUiUnit,
} from "../../models";

/** Live map page: grid/map polling timestamps, counters, and in-flight flags (single source for UI + context). */
export interface ILiveMapPollingUiState {
  lastSynchroGridDate?: TDateISO;
  lastSynchroMapDate?: TDateISO;
  isGridPollingRunning: boolean;
  mapPollingRideIds: readonly number[] | undefined;
  gridPoolingCount: number;
  mapPoolingCount: number;
  isGridPollFetchInFlight: boolean;
  isMapPollFetchInFlight: boolean;
}

export const defaultLiveMapPollingUiState: ILiveMapPollingUiState = {
  lastSynchroGridDate: undefined,
  lastSynchroMapDate: undefined,
  isGridPollingRunning: false,
  mapPollingRideIds: undefined,
  gridPoolingCount: 0,
  mapPoolingCount: 0,
  isGridPollFetchInFlight: false,
  isMapPollFetchInFlight: false,
};

export const liveMapPollingUiAtom = atom<ILiveMapPollingUiState>({
  key: "liveMapPollingUiAtom",
  default: defaultLiveMapPollingUiState,
});

/** While true, live map fitBounds is skipped during bulk grid selection. */
export const liveMapAutofitSuspendedAtom = atom<boolean>({
  key: "liveMapAutofitSuspendedAtom",
  default: false,
});

/** Bumped once after bulk selection ends to run a single fitBounds. */
export const liveMapAutofitPulseAtom = atom<number>({
  key: "liveMapAutofitPulseAtom",
  default: 0,
});

/** Session-only user override of liveMapHoursBack (the system param remains the fallback). */
export const liveMapHoursBackOverrideAtom = atom<number | undefined>({
  key: "liveMapHoursBackOverrideAtom",
  default: undefined,
});

/** Session-only user override of liveMapHoursForward (the system param remains the fallback). */
export const liveMapHoursForwardOverrideAtom = atom<number | undefined>({
  key: "liveMapHoursForwardOverrideAtom",
  default: undefined,
});

/** Session-only user override of ridesMapDefaultDisplayMode (the system param remains the fallback). */
export const liveMapDisplayModeOverrideAtom = atom<
  LiveMapDisplayModeEnum | undefined
>({
  key: "liveMapDisplayModeOverrideAtom",
  default: undefined,
});

/** Session-only user override of liveMapRefreshGridIntervalSec (resets on page reload). */
export const liveMapRefreshGridIntervalSecOverrideAtom = atom<
  number | undefined
>({
  key: "liveMapRefreshGridIntervalSecOverrideAtom",
  default: undefined,
});

/** Session-only user override of liveMapRefreshMapIntervalSec (resets on page reload). */
export const liveMapRefreshMapIntervalSecOverrideAtom = atom<
  number | undefined
>({
  key: "liveMapRefreshMapIntervalSecOverrideAtom",
  default: undefined,
});

/** Live map grid: delay-status groups hidden via toolbar chips (empty = all visible). */
export const liveMapHiddenDelayStatusesAtomFamily = atomFamily<
  readonly RideDelayStatusEnum[],
  RydeUiUnit
>({
  key: "liveMapHiddenDelayStatusesAtomFamily",
  default: [],
});

/** Live map: server filter — rides currently within their planned start/end window. */
export const liveMapPlannedForNowAtomFamily = atomFamily<boolean, RydeUiUnit>({
  key: "liveMapPlannedForNowAtomFamily",
  default: false,
});

/** Live map grid toolbar: delay-status bucket filter (sent to rides/search). */
export const liveMapRideFilterAtomFamily = atomFamily<
  LiveMapRideFilterEnum,
  RydeUiUnit
>({
  key: "liveMapRideFilterAtomFamily",
  default: DEFAULT_LIVE_MAP_RIDE_FILTER,
});

/** Mobile live map: which full-screen surface is active (grid or map). */
export const liveMapMobileSurfaceAtom = atom<LiveMapMobileSurfaceEnum>({
  key: "liveMapMobileSurfaceAtom",
  default: LiveMapMobileSurfaceEnum.Grid,
});

/** Latest toolbar counters from live-map `rides/search` (`rows[0].computedData.liveMapStats`). */
export const liveMapGridToolbarStatsAtomFamily = atomFamily<
  ILiveMapGridToolbarStats,
  RydeUiUnit
>({
  key: "liveMapGridToolbarStatsAtomFamily",
  default: emptyLiveMapGridToolbarStats,
});

/**
 * Deep Ride Investigation: the ride open in the overlay (undefined = closed). Deliberately GLOBAL, not keyed
 * by page entity — the ride edit drawer is mounted outside every PageContext, so an entity-keyed atom would
 * silently write the "unknown" slot from there and nothing would open. One investigation at a time anyway:
 * the layer toggles below are global too.
 */
export const rideInvestigationRideIdAtom = atom<number | undefined>({
  key: "rideInvestigationRideIdAtom",
  default: undefined,
});

/** Deep Ride Investigation map layer toggles (top-bar filters). */
export const investigationShowTrackingAtom = atom<boolean>({
  key: "investigationShowTrackingAtom",
  default: true,
});

export const investigationShowNoGpsAtom = atom<boolean>({
  key: "investigationShowNoGpsAtom",
  default: true,
});

export const investigationShowDetourAtom = atom<boolean>({
  key: "investigationShowDetourAtom",
  default: true,
});

export const investigationShowPlannedAtom = atom<boolean>({
  key: "investigationShowPlannedAtom",
  default: true,
});

export const investigationShowStationsAtom = atom<boolean>({
  key: "investigationShowStationsAtom",
  default: true,
});

/** When true, the animated playback vehicle (car) marker is shown on the map. */
export const investigationShowCarAtom = atom<boolean>({
  key: "investigationShowCarAtom",
  default: true,
});

/** Stops the selected Timeline row is about, ringed on the investigation map; empty when no row is selected. */
export const investigationHighlightedStationIdsAtom = atom<number[]>({
  key: "investigationHighlightedStationIdsAtom",
  default: [],
});

/**
 * When true, the map keeps the playback vehicle on screen while the ride plays. Off the moment the
 * user drags the map — a camera that pans back would fight them — and re-armed from the transport bar.
 */
export const investigationFollowVehicleAtom = atom<boolean>({
  key: "investigationFollowVehicleAtom",
  default: true,
});

/** When true, station markers show their sequence number; when false, plain bullets. */
export const investigationStationLabelsAtom = atom<boolean>({
  key: "investigationStationLabelsAtom",
  default: true,
});
